/**
 * Shared controls, in the mockup's "soft raised" family: gentle gradients,
 * rounded corners, thin shadows. Sizes are the design's own — a 26px control
 * row and a 28px icon button — because the screens depend on that density.
 */

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "./icons";

/* ---------------------------------------------------------------- Panel */

/**
 * Sebuah panel, dengan kepalanya, dan bisa dilipat bila pemanggilnya
 * memberikan `onCollapsedChange`.
 *
 * Lipatannya dikendalikan dari luar — panel ini tidak menyimpan keadaannya
 * sendiri — dengan alasan yang sama seperti rel-rel layar: keadaan yang
 * pantas dilipat pantas pula bertahan sesudah halaman dimuat ulang, dan yang
 * tahu ke mana ia disimpan adalah layarnya, bukan panelnya.
 *
 * Ketika terbuka, hanya caret di kepalanya yang melipat; ketika terlipat,
 * seluruh kepala itu bisa ditekan. Sebuah panel yang hilang karena klik yang
 * meleset adalah panel yang tidak bisa dipercaya — tetapi sebuah kepala
 * setinggi 26 piksel yang hanya bisa dibuka lewat caret selebar 18 piksel
 * adalah sasaran yang dicari-cari, dan di keadaan itu tidak ada apa pun di
 * bawahnya yang bisa hilang karena tertekan.
 */
export function Panel({
  title,
  meta,
  children,
  actions,
  flush,
  style,
  bodyStyle,
  collapsed = false,
  onCollapsedChange,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  /** Hanya berarti bersama `onCollapsedChange`. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}) {
  const collapsible = Boolean(onCollapsedChange);
  const folded = collapsible && collapsed;
  // Namanya menyebutkan arah geraknya, dan menyebut panel mana yang
  // dimaksudnya bila judulnya memang sebuah kata: "Sembunyikan isi panel"
  // pada layar berisi enam panel tidak mengatakan yang mana.
  const toggleLabel =
    `${folded ? "Show" : "Hide"} panel contents${typeof title === "string" ? ` ${title}` : ""}`;
  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        // Panels stack inside scrolling flex columns; without `flex: none` they
        // would shrink to fit and silently clip their own last rows.
        flex: "none",
        minWidth: 0,
        background: "var(--panel)",
        border: "1px solid var(--border-control)",
        borderRadius: "var(--radius-panel)",
        boxShadow: "var(--shadow-panel)",
        overflow: "hidden",
        ...style,
      }}
    >
      {title !== undefined && (
        <header
          onClick={folded ? () => onCollapsedChange!(false) : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 11px",
            background: "var(--panel-header)",
            // Garis pemisah kepala dan isinya hanya ada selama ada isi yang
            // dipisahkan.
            borderBottom: folded ? "none" : "1px solid var(--line-strong)",
            cursor: folded ? "pointer" : undefined,
          }}
        >
          <span
            style={{
              font: "600 9.5px var(--font-mono)",
              color: "var(--ink-2)",
              letterSpacing: ".09em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </span>
          <span style={{ flex: 1 }} />
          {meta && <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>{meta}</span>}
          {actions}
          {collapsible && (
            <button
              type="button"
              title={toggleLabel}
              aria-label={toggleLabel}
              aria-expanded={!folded}
              onClick={(event) => {
                // Kepala yang terlipat sudah menangani kliknya sendiri.
                event.stopPropagation();
                onCollapsedChange!(!folded);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
                flex: "none",
                padding: 0,
                border: "none",
                background: "transparent",
                color: "var(--ink-5)",
                cursor: "pointer",
              }}
            >
              {/* `back` menunjuk ke kiri: diputar ke atas ketika isinya akan
                  ditarik naik, ke bawah ketika ia akan turun kembali. */}
              <span
                style={{
                  display: "flex",
                  transform: `rotate(${folded ? -90 : 90}deg)`,
                }}
              >
                {Icon.back({ size: 11 })}
              </span>
            </button>
          )}
        </header>
      )}
      {!folded && (
        <div
          style={{
            padding: flush ? 0 : 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minHeight: 0,
            ...bodyStyle,
          }}
        >
          {children}
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------------------- Button */

type ButtonVariant = "primary" | "default" | "ghost" | "danger";

export function Button({
  children,
  onClick,
  variant = "default",
  disabled,
  busy,
  title,
  full,
  type = "button",
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  busy?: boolean;
  title?: string;
  full?: boolean;
  type?: "button" | "submit";
  style?: CSSProperties;
}) {
  const [hover, setHover] = useState(false);
  const inert = disabled || busy;
  const palette: Record<ButtonVariant, CSSProperties> = {
    primary: {
      background: "linear-gradient(var(--accent-bright), var(--accent-deep))",
      border: "1px solid var(--accent-edge)",
      color: "var(--accent-contrast)",
      boxShadow: "var(--shadow-accent)",
      fontWeight: 600,
    },
    default: {
      background: "var(--control)",
      border: "1px solid var(--border-control)",
      color: "var(--ink-2)",
      boxShadow: "var(--shadow-control)",
      fontWeight: 500,
    },
    ghost: {
      background: hover && !inert ? "var(--hover-soft)" : "transparent",
      border: "1px solid var(--border-control)",
      color: hover && !inert ? "var(--ink)" : "var(--ink-3)",
      fontWeight: 500,
    },
    danger: {
      background: "transparent",
      border: "1px solid var(--rose)",
      color: "var(--rose)",
      fontWeight: 500,
    },
  };
  return (
    <button
      type={type}
      title={title}
      disabled={inert}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        height: 31,
        padding: "0 14px",
        width: full ? "100%" : undefined,
        borderRadius: "var(--radius)",
        fontSize: 11.5,
        cursor: inert ? "not-allowed" : "pointer",
        opacity: inert ? 0.55 : 1,
        filter: hover && !inert && variant === "primary" ? "brightness(1.08)" : undefined,
        transition: "filter .12s, background .12s, color .12s",
        whiteSpace: "nowrap",
        ...palette[variant],
        ...style,
      }}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

export function IconButton({
  children,
  onClick,
  active,
  title,
  size = 28,
}: {
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  title: string;
  size?: number;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flex: "none",
        borderRadius: "var(--radius)",
        cursor: "pointer",
        background: active ? "var(--accent-wash)" : "var(--control)",
        border: `1px solid ${active ? "var(--accent)" : hover ? "var(--border-hover)" : "var(--border-control)"}`,
        boxShadow: "var(--shadow-control)",
        color: active ? "var(--accent-pale)" : "var(--ink-2)",
      }}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
      <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.6" fill="none" opacity=".28" />
      <path d="M6 1.4 A4.6 4.6 0 0 1 10.6 6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 6 6"
          to="360 6 6"
          dur="0.7s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}

/* ------------------------------------------------------------- Dropdown */

export interface Option<T = string> {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export function Dropdown<T extends string | number>({
  value,
  options,
  onChange,
  label,
  ariaLabel,
  width,
  accent,
  compact,
  disabled,
}: {
  value: T;
  options: Option<T>[];
  onChange: (value: T) => void;
  label?: string;
  /**
   * Nama tombolnya bagi yang tidak melihatnya.
   *
   * Tanpa ini nama sebuah `Dropdown` adalah **opsi yang sedang terpilih** —
   * `label` di sebelahnya hanya sebuah `<span>` yang tidak ditautkan ke mana
   * pun. Sebuah tombol bernama "RT" tidak mengatakan bahwa yang dipilihnya
   * adalah kurva yang dibaca sebagai RT, dan di deretan pemilih yang nilainya
   * kebetulan sama ia bahkan tidak bisa dibedakan dari tetangganya.
   */
  ariaLabel?: string;
  width?: number | string;
  accent?: boolean;
  compact?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = useId();
  const current = options.find((option) => option.value === value);

  /**
   * Di mana daftarnya digambar, dalam koordinat layar.
   *
   * Daftar yang terbuka tidak lagi menjadi anak dari kotaknya melainkan
   * digantung di `document.body` — lihat `createPortal` di bawah — dan karena
   * itu kedudukannya harus dihitung, bukan diwariskan.
   *
   * Sebabnya sebuah cacat yang selama ini membuat separuh dropdown aplikasi
   * ini tidak bisa dipakai sama sekali: `Panel` menggunting isinya
   * (`overflow: hidden`, demi sudut tumpulnya), jadi sebuah dropdown yang
   * duduk paling bawah di sebuah panel membuka daftarnya **di luar** panel itu
   * dan yang tampak hanyalah baris pertamanya. "New track from curve…" di
   * lembar log menawarkan dua puluh enam kurva dan memperlihatkan nol di
   * antaranya; dari luar ia terbaca seperti daftar yang memang kosong.
   * Menggulir tidak menolong — yang memotongnya kotak panelnya, bukan tepi
   * layar — dan satu-satunya jalan adalah keluar dari kotak itu.
   */
  const [at, setAt] = useState<
    { left: number; right: number; top: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) { setAt(null); return; }
    const place = () => {
      const box = button.current?.getBoundingClientRect();
      if (box) {
        setAt({ left: box.left, right: box.right, top: box.top, bottom: box.bottom });
      }
    };
    place();
    // Ditempatkan ulang selama halaman bergeser, dan bukan ditutup: rel
    // setelan bergulir sendiri ketika sebuah panel dibuka di bawahnya, dan
    // daftar yang menutup dirinya karena itu terbaca seperti klik yang tidak
    // mendarat. `capture` sebab yang bergulir adalah wadah di dalam halaman,
    // dan geserannya tidak menggelembung ke `window`.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      // Daftarnya di luar `ref` sejak ia digantung di `body`, jadi ia harus
      // disebut sendiri. Tanpa itu penekanan pada sebuah pilihan dibaca
      // sebagai penekanan di luar: daftarnya lenyap sebelum `click` tiba, dan
      // tidak ada satu pun pilihan yang bisa dipilih.
      if (!ref.current?.contains(target) && !list.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, position: "relative" }} ref={ref}>
      {label && (
        <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".06em" }}>
          {label}
        </span>
      )}
      <button
        ref={button}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          height: compact ? 25 : 27,
          padding: "0 9px",
          minWidth: width ?? 150,
          width: width,
          background: "var(--control)",
          border: `1px solid ${accent ? "var(--accent)" : "var(--border-control)"}`,
          borderRadius: "var(--radius)",
          boxShadow: "var(--shadow-control)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          color: "var(--ink)",
          font: "500 11.5px var(--font-sans)",
          textAlign: "left",
        }}
      >
        <span
          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {current?.label ?? "—"}
        </span>
        <Caret color={accent ? "var(--accent)" : "var(--ink-4)"} />
      </button>
      {open && at && createPortal(
        <ul
          ref={list}
          id={listId}
          role="listbox"
          style={{
            // Digantung di `body` dan bukan di dalam kotaknya, supaya panel
            // yang menggunting isinya tidak ikut menggunting daftar ini —
            // lihat `at` di atas. Karena itu pula ia `fixed`: koordinatnya
            // koordinat layar, dan yang dipatok tepi kanan tombolnya supaya
            // daftar yang lebih lebar tumbuh ke kiri seperti sebelumnya.
            position: "fixed",
            ...(spaceBelow(at) >= 160 || spaceBelow(at) >= spaceAbove(at)
              ? { top: at.bottom + 5, maxHeight: Math.min(300, spaceBelow(at)) }
              : { bottom: window.innerHeight - at.top + 5,
                  maxHeight: Math.min(300, spaceAbove(at)) }),
            // Tepi kanan tombolnya yang dipatok, sehingga daftar yang lebih
            // lebar tumbuh ke kiri — kecuali di dekat tepi kiri layar, tempat
            // pertumbuhan itu justru membuang bagian depan tiap namanya.
            ...(at.right < 326
              ? { left: 6 }
              : { right: Math.max(6, window.innerWidth - at.right) }),
            // Lebar terkecilnya diukur dari tombolnya dan bukan diwarisi
            // darinya. `width` boleh berupa "100%", dan seratus persen dari
            // sebuah elemen `fixed` adalah seratus persen **layar**: daftarnya
            // membentang selebar jendela dan tumbuh ke kiri sampai keluar
            // darinya, sehingga nama tiap pilihan terpotong di tepi kiri.
            minWidth: Math.max(at.right - at.left,
                               typeof width === "number" ? width : 180),
            // Daftarnya boleh lebih lebar daripada tombolnya. Tanpa ini ia
            // mewarisi lebar tombol — dan sebuah tombol selebar 92 px, yang
            // memang cukup untuk menampung "TVDSS", memaksa keterangan tiap
            // pilihan terlipat dan terpotong justru pada dropdown yang
            // keterangannya paling dibutuhkan: yang membuka daftar acuan
            // kedalaman adalah orang yang belum hafal bedanya TVD dan TVDRT.
            // Ia tumbuh ke kiri, sebab tepi kanannya yang dipatok.
            width: "max-content",
            maxWidth: 320,
            listStyle: "none",
            margin: 0,
            padding: 5,
            background: "var(--panel)",
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius-panel)",
            boxShadow: "var(--shadow-float)",
            // Di atas jendela melayang aplikasi ini — lembar log LRLC berdiri
            // pada 110, kotak pembacanya pada 130 — sebab sebuah daftar yang
            // terbuka di belakang jendela yang memuatnya sama saja dengan
            // daftar yang tidak terbuka.
            zIndex: 1000,
            overflowY: "auto",
          }}
        >
          {options.map((option) => (
            <li key={String(option.value)}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  width: "100%",
                  padding: "6px 9px",
                  borderRadius: 5,
                  border: "none",
                  background: option.value === value ? "var(--selected)" : "transparent",
                  color: option.disabled
                    ? "var(--ink-7)"
                    : option.value === value
                      ? "var(--ink-strong)"
                      : "var(--ink-2)",
                  font: "400 11.5px var(--font-sans)",
                  cursor: option.disabled ? "not-allowed" : "pointer",
                  textAlign: "left",
                }}
              >
                {option.value === value && <Tick />}
                <span style={{ flex: 1, whiteSpace: "nowrap" }}>{option.label}</span>
                {option.hint && (
                  <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)",
                                 whiteSpace: "nowrap" }}>
                    {option.hint}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}

/** Ruang yang tersisa di bawah dan di atas sebuah tombol, sampai tepi layar. */
function spaceBelow(at: { bottom: number }): number {
  return window.innerHeight - at.bottom - 12;
}

function spaceAbove(at: { top: number }): number {
  return at.top - 12;
}

/** Keterangan sebaris di atas sebuah kendali rel, segaya dengan NumberField. */
export function ParamCaption({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        font: "500 9px var(--font-mono)",
        color: "var(--ink-6)",
        letterSpacing: ".06em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Kendali berbentuk kotak metode: melaporkan pilihannya, dan membukanya.
 *
 * Dipakai oleh kartu tahap di rel alur deterministik. Bentuknya sengaja sama
 * dengan kotak yang hanya melaporkan — sebuah tahap yang bisa disetel dan
 * sebuah tahap yang tidak duduk berjajar di rel yang sama, dan caret itulah
 * satu-satunya yang membedakan keduanya. Isinya bebas karena tiap tahap
 * mencetak metodenya dengan caranya sendiri.
 */
export function MethodButton({
  children,
  onClick,
  title,
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        height: 25,
        padding: "0 8px",
        background: "var(--control)",
        border: "1px solid var(--border-control)",
        borderRadius: "var(--radius)",
        font: "500 10.5px var(--font-sans)",
        color: "var(--ink)",
        textAlign: "left",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </span>
      {!disabled && <Caret />}
    </button>
  );
}

export function Caret({ color = "var(--ink-4)", size = 8 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size * 0.625} viewBox="0 0 8 5" style={{ flex: "none" }} aria-hidden>
      <path d="M0 0 L4 5 L8 0 Z" fill={color} />
    </svg>
  );
}

function Tick() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" aria-hidden>
      <path d="M1 5 L3.4 7.4 L8 2" stroke="var(--accent-bright)" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

/* --------------------------------------------------------------- Inputs */

export function NumberField({
  label,
  unit,
  value,
  onChange,
  step = 0.01,
  min,
  max,
  disabled,
}: {
  label: string;
  unit?: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}) {
  return (
    <NumberInput
      label={label}
      unit={unit}
      value={value}
      step={step}
      min={min}
      max={max}
      disabled={disabled}
      onCommit={(next) => next !== null && onChange(next)}
    />
  );
}

/**
 * A number field that is allowed to be empty, and means it.
 *
 * Several parameters are genuinely optional — the top of a fitting interval,
 * an upper cutoff — and for those, zero is not the same answer as blank. A
 * plain NumberField cannot say "blank", so it would have to pick a number on
 * the operator's behalf and then act on it.
 */
export function OptionalNumberField({
  label,
  unit,
  value,
  onChange,
  step = 0.01,
  min,
  max,
  placeholder = "—",
}: {
  label: string;
  unit?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}) {
  return (
    <NumberInput
      label={label}
      unit={unit}
      value={value}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      onCommit={onChange}
    />
  );
}

function NumberInput({
  label,
  unit,
  value,
  onCommit,
  step = 0.01,
  min,
  max,
  disabled,
  placeholder,
}: {
  label: string;
  unit?: string;
  value: number | null;
  onCommit: (value: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  placeholder?: string;
}) {
  // Editing is held as text so a half-typed "0." or "-" is not clobbered.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value === null ? "" : String(value));
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span
        style={{
          font: "400 10px var(--font-mono)",
          color: "var(--ink-5)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 26,
          minWidth: 0,
          padding: "0 8px",
          background: "var(--panel-inset)",
          border: "1px solid var(--line-strong)",
          borderRadius: 5,
        }}
      >
        <input
          type="number"
          value={shown}
          step={step}
          min={min}
          max={max}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const parsed = Number(shown);
            if (shown.trim() === "") onCommit(null);
            else if (Number.isFinite(parsed)) onCommit(parsed);
            setDraft(null);
          }}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            font: "500 11px var(--font-mono)",
            color: "var(--ink)",
          }}
        />
        {unit && <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-7)" }}>{unit}</span>}
      </span>
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)", letterSpacing: ".04em" }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
        style={{
          height: 32,
          padding: "0 10px",
          background: "var(--panel-inset)",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--radius)",
          font: "400 12.5px var(--font-sans)",
          color: "var(--ink)",
          outline: "none",
        }}
      />
    </label>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        width: "100%",
        background: "none",
        border: "none",
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-2)", textAlign: "left" }}>{label}</span>
      <span
        style={{
          position: "relative",
          width: 28,
          height: 15,
          flex: "none",
          borderRadius: 9,
          background: checked ? "var(--accent)" : "var(--line)",
          transition: "background .15s",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 15 : 2,
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: checked ? "var(--accent-contrast)" : "var(--ink-6)",
            transition: "left .15s",
          }}
        />
      </span>
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  dot,
  meta,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  dot?: string;
  meta?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        opacity: disabled ? 0.55 : 1,
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        padding: "6px 8px",
        borderRadius: "var(--radius)",
        background: "transparent",
        border: "none",
        cursor: disabled ? "default" : "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 13,
          height: 13,
          flex: "none",
          borderRadius: 4,
          background: checked ? "linear-gradient(var(--accent-bright), var(--accent-deep))" : "var(--panel-inset)",
          border: `1px solid ${checked ? "var(--accent-edge)" : "var(--border-control)"}`,
        }}
      >
        {checked && (
          <svg width="8" height="8" viewBox="0 0 9 9" aria-hidden>
            <path d="M1 5 L3.4 7.4 L8 2" stroke="var(--accent-contrast)" strokeWidth="1.7" fill="none" />
          </svg>
        )}
      </span>
      {dot && (
        <span style={{ width: 6, height: 6, borderRadius: 2, background: dot, flex: "none" }} />
      )}
      <span
        style={{
          font: `${checked ? 500 : 400} 11px var(--font-sans)`,
          color: checked ? "var(--ink)" : "var(--ink-5)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1 }} />
      {meta && (
        <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-7)", whiteSpace: "nowrap" }}>{meta}</span>
      )}
    </button>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 0.01,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  format?: (value: number) => string;
}) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span className="label">{label}</span>
        <span style={{ font: "500 11px var(--font-mono)", color: "var(--accent)" }}>
          {format ? format(value) : value.toFixed(2)}
        </span>
      </div>
      <div style={{ position: "relative", height: 16, display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", left: 0, right: 0, height: 4, background: "var(--line)", borderRadius: 3 }} />
        <div
          style={{
            position: "absolute",
            left: 0,
            width: `${percent}%`,
            height: 4,
            background: "var(--accent)",
            borderRadius: 3,
          }}
        />
        <div
          style={{
            position: "absolute",
            left: `${percent}%`,
            width: 12,
            height: 12,
            marginLeft: -6,
            background: "var(--ink)",
            border: "2px solid var(--accent)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <input
          type="range"
          aria-label={label}
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(event) => onChange(Number(event.target.value))}
          style={{ position: "absolute", inset: 0, width: "100%", opacity: 0, cursor: "pointer", margin: 0 }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Segmented */

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = "md",
  wrap,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  size?: "sm" | "md";
  /**
   * Pilihan yang tidak muat sebaris turun ke baris berikutnya.
   *
   * Tidak dinyalakan di mana-mana, sebab satu baris memang yang benar bagi
   * hampir seluruh pemakaiannya: dua atau tiga kata pendek di lebar layar
   * penuh. Yang memerlukannya adalah pilihan berlabel panjang di dalam rel
   * sempit, dan di sana ketiadaannya tidak terlihat sebagai luapan melainkan
   * sebagai **pilihan yang hilang**. `overflow: hidden` di bawah memotong yang
   * tidak muat, dan menekan salah satu pilihannya menggulirkan strip itu
   * sendiri untuk memperlihatkan yang baru difokus — jadi pilihan pertama
   * terpotong dari kiri persis pada saat seseorang memakai yang ketiga.
   * "Whole depth" yang terbaca "ole depth" bukan tata letak yang sesak,
   * melainkan satu-satunya jalan kembali ke nilai seluruh-kedalaman yang
   * tampak seperti tidak ada.
   */
  wrap?: boolean;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        flexWrap: wrap ? "wrap" : undefined,
        rowGap: wrap ? 2 : undefined,
        background: "var(--control-flat)",
        border: "1px solid var(--border-control)",
        borderRadius: 7,
        overflow: "hidden",
        padding: 2,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              padding: size === "sm" ? "3px 9px" : "4px 11px",
              border: "none",
              borderRadius: 5,
              background: active ? "var(--selected)" : "transparent",
              color: active ? "var(--ink-strong)" : "var(--ink-4)",
              font: `${active ? 600 : 500} ${size === "sm" ? 10 : 10.5}px var(--font-sans)`,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Chip({
  children,
  active,
  onClick,
  color,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  color?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 9px",
        borderRadius: 12,
        background: active ? "var(--accent-wash)" : "transparent",
        border: `1px solid ${active ? "var(--accent)" : "var(--border-control)"}`,
        color: active ? "var(--accent-pale)" : "var(--ink-4)",
        font: "500 10.5px var(--font-sans)",
        cursor: onClick ? "pointer" : "default",
        whiteSpace: "nowrap",
      }}
    >
      {color && <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------- Feedback */

export function Notice({
  children,
  tone = "warn",
}: {
  children: ReactNode;
  tone?: "warn" | "error" | "info";
}) {
  const palette = {
    warn: { bg: "var(--warn-bg)", line: "var(--warn-line)", ink: "var(--warn-ink)", stroke: "var(--amber)" },
    error: { bg: "transparent", line: "var(--rose)", ink: "var(--rose)", stroke: "var(--rose)" },
    info: { bg: "var(--hover-soft)", line: "var(--border-control)", ink: "var(--ink-3)", stroke: "var(--accent)" },
  }[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        flex: "none",
        padding: "9px 11px",
        background: palette.bg,
        border: `1px solid ${palette.line}`,
        borderRadius: "var(--radius-panel)",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" style={{ flex: "none", marginTop: 1 }} aria-hidden>
        <path d="M6 1 L11.2 10.6 H0.8 Z" stroke={palette.stroke} strokeWidth="1.2" fill="none" />
        <path d="M6 4.6v3" stroke={palette.stroke} strokeWidth="1.2" />
      </svg>
      <span style={{ font: "400 10.5px/1.4 var(--font-sans)", color: palette.ink }}>{children}</span>
    </div>
  );
}

export function StatusDot({ tone }: { tone: "ok" | "warn" | "error" | "idle" }) {
  const color = { ok: "var(--green)", warn: "var(--amber)", error: "var(--rose)", idle: "var(--ink-7)" }[tone];
  return <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flex: "none" }} />;
}

export function KeyValue({ k, v, mono = true }: { k: ReactNode; v: ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
      <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-4)" }}>{k}</span>
      <span
        style={{
          font: mono ? "500 10.5px var(--font-mono)" : "500 10.5px var(--font-sans)",
          color: "var(--ink)",
          textAlign: "right",
        }}
      >
        {v}
      </span>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: 32,
        color: "var(--ink-5)",
        font: "400 12px var(--font-sans)",
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 9,
        padding: 28,
        color: "var(--ink-5)",
        font: "400 11.5px var(--font-mono)",
      }}
    >
      <Spinner />
      {label}
    </div>
  );
}

/* ----------------------------------------------------------- Context menu */

export function ContextMenu({
  x,
  y,
  title,
  items,
  onClose,
}: {
  x: number;
  y: number;
  title?: string;
  items: { label: string; onClick?: () => void; danger?: boolean; separator?: boolean; icon?: ReactNode }[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        position: "fixed",
        left: Math.min(x, window.innerWidth - 230),
        top: Math.min(y, window.innerHeight - 260),
        width: 214,
        background: "var(--panel)",
        border: "1px solid var(--border-control)",
        borderRadius: "var(--radius-panel)",
        boxShadow: "var(--shadow-float)",
        padding: 5,
        zIndex: 80,
      }}
    >
      {title && (
        <div
          style={{
            padding: "4px 9px 6px",
            font: "600 9px var(--font-mono)",
            color: "var(--ink-6)",
            letterSpacing: ".08em",
          }}
        >
          {title}
        </div>
      )}
      {items.map((item, index) =>
        item.separator ? (
          <div key={index} style={{ height: 1, background: "var(--line-strong)", margin: "5px 6px" }} />
        ) : (
          <button
            key={index}
            role="menuitem"
            type="button"
            onClick={() => {
              item.onClick?.();
              onClose();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "6px 9px",
              border: "none",
              borderRadius: 5,
              background: "transparent",
              color: item.danger ? "var(--rose)" : "var(--ink-2)",
              font: "400 11.5px var(--font-sans)",
              cursor: "pointer",
              textAlign: "left",
            }}
            onMouseEnter={(event) => (event.currentTarget.style.background = "var(--selected)")}
            onMouseLeave={(event) => (event.currentTarget.style.background = "transparent")}
          >
            {item.icon}
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Modal */

export function Modal({
  title,
  children,
  onClose,
  width = 460,
  footer,
  onResize,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: number;
  footer?: ReactNode;
  /**
   * Membuat jendela ini bisa diubah ukurannya, lewat pegangan di sudut kanan
   * bawahnya. Yang dikirim adalah simpangan tapak, dan yang memutuskan berapa
   * lebar dan tinggi jadinya adalah pemanggilnya — jendela ini tidak tahu
   * bagian mana dari isinya yang pantas tumbuh.
   *
   * Simpangannya dikirim apa adanya; jendela ini duduk di tengah layar, jadi
   * pemanggil yang ingin sudutnya tetap menempel pada tapak menggandakannya.
   */
  onResize?: (dx: number, dy: number) => void;
}) {
  useEffect(() => {
    const escape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
        style={{
          width,
          maxWidth: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          background: "var(--panel)",
          border: "1px solid var(--border-control)",
          borderRadius: "var(--radius-panel)",
          boxShadow: "var(--shadow-float)",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 13px",
            background: "var(--panel-header)",
            borderBottom: "1px solid var(--line-strong)",
          }}
        >
          <span style={{ font: "600 12px var(--font-sans)", color: "var(--ink)" }}>{title}</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2 }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
              <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </header>
        <div className="scroll-y" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          {children}
        </div>
        {footer && (
          <footer
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              padding: "11px 13px",
              borderTop: "1px solid var(--line-strong)",
            }}
          >
            {footer}
          </footer>
        )}
        {onResize && <ModalGrip onResize={onResize} />}
      </div>
    </div>
  );
}

/**
 * Pegangan ubah-ukuran di sudut kanan bawah sebuah jendela.
 *
 * Tapaknya ditangkap, sebab sudut ini kecil dan tangan yang menyeret cepat
 * meninggalkannya lebih dulu daripada niatnya; tanpa tangkapan, jendela
 * berhenti tumbuh di tengah seretan tanpa alasan yang terlihat.
 */
function ModalGrip({ onResize }: { onResize: (dx: number, dy: number) => void }) {
  const last = useRef<{ x: number; y: number } | null>(null);
  return (
    <div
      role="separator"
      aria-label="Resize window"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        last.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!last.current) return;
        onResize(event.clientX - last.current.x, event.clientY - last.current.y);
        last.current = { x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        last.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => (last.current = null)}
      style={{
        position: "absolute",
        right: 0,
        bottom: 0,
        width: 16,
        height: 16,
        cursor: "nwse-resize",
        touchAction: "none",
        // Tiga garis miring, sebagaimana sudut jendela mana pun: sebuah sudut
        // yang bisa diseret tanpa tanda adalah kemampuan yang hanya ditemukan
        // oleh orang yang sudah tahu ia ada.
        backgroundImage:
          "linear-gradient(135deg, transparent 45%, var(--ink-7) 45%, var(--ink-7) 55%, transparent 55%)",
        backgroundSize: "5px 5px",
      }}
    />
  );
}

/* --------------------------------------------------------------- Toasts */

type Toast = { id: number; message: string; tone: "ok" | "error" };
const ToastContext = createContext<(message: string, tone?: "ok" | "error") => void>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = (message: string, tone: "ok" | "error" = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4800);
  };
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          zIndex: 200,
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            style={{
              maxWidth: 380,
              padding: "10px 13px",
              background: "var(--panel)",
              border: `1px solid ${toast.tone === "error" ? "var(--rose)" : "var(--accent)"}`,
              borderRadius: "var(--radius-panel)",
              boxShadow: "var(--shadow-float)",
              font: "400 11.5px/1.4 var(--font-sans)",
              color: toast.tone === "error" ? "var(--rose)" : "var(--ink)",
            }}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
